import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { WalletProvider } from './contexts/WalletContext';
import { TOAST_CONFIG } from './config/constants';

// Components
import Header from './components/Header/Header';
import BridgeInterface from './components/Bridge/BridgeInterface';
import TransactionHistory from './components/History/TransactionHistory';

// Pages
import NotFound from './pages/NotFound';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <WalletProvider>
        <Router>
          <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
            {/* Toast Notifications */}
            <Toaster
              position={TOAST_CONFIG.position}
              toastOptions={{
                duration: TOAST_CONFIG.duration,
                style: TOAST_CONFIG.style,
              }}
            />

            {/* Header */}
            <Header />

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <Routes>
                {/* Bridge Page */}
                <Route 
                  path="/" 
                  element={
                    <div className="max-w-2xl mx-auto">
                      <BridgeInterface />
                    </div>
                  } 
                />

                {/* Transaction History */}
                <Route 
                  path="/history" 
                  element={<TransactionHistory />} 
                />

                {/* 404 Page */}
                <Route 
                  path="*" 
                  element={<NotFound />} 
                />
              </Routes>
            </main>

            {/* Footer */}
            <footer className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-t border-slate-200 dark:border-slate-700 mt-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">PF</span>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-100">
                        PFVS Bridge
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        Secure cross-chain token transfers
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6 text-sm text-slate-600 dark:text-slate-400">
                    <a 
                      href="https://docs.pfvs.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                    >
                      Documentation
                    </a>
                    <a 
                      href="https://github.com/pfvs/bridge" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                    >
                      GitHub
                    </a>
                    <a 
                      href="https://discord.gg/pfvs" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                    >
                      Support
                    </a>
                  </div>
                </div>

                <div className="mt-8 pt-4 border-t border-slate-200 dark:border-slate-700 text-center text-sm text-slate-600 dark:text-slate-400">
                  <p>
                    © 2025 PFVS Bridge. Built with security and reliability in mind.
                  </p>
                  <p className="mt-1">
                    Multi-relayer validation • Real-time monitoring • Secure by design
                  </p>
                </div>
              </div>
            </footer>
          </div>
        </Router>
      </WalletProvider>
    </QueryClientProvider>
  );
}

export default App; 